/* eslint-disable no-shadow */
import { createSelector } from '@reduxjs/toolkit';

import { TERMS_AND_CONDITIONS_LAST_UPDATED as dateTermsLastUpdated } from 'utils/constants';
import { RADIUS_TO_SLIDER_VALUE } from '../filters/filterConstants';
import { CONFIG_SLICE_NAME } from '../slicesNames';
import { MAX_DAYS_BETWEEN_TERMS_ACCEPTANCE } from './configConstants';
import { currentSlug } from './selectNetworks';
import { requireReferringClinic, referringClinic } from '../referringClinic/selectReferringClinic';
import * as selectSession from '../session/selectSession';

/** The entire configSlice object. Should be used sparingly, prefer more granular selectors
 * whenever possible.
 * @returns {Object} configSlice object */
export const slice = (state) => state[CONFIG_SLICE_NAME];

/**  The state of the client config fetch call
 * @returns {boolean} True while the fetch call is taking place */
export const isLoading = (state) => state[CONFIG_SLICE_NAME].isLoading;

/** This indicates that the App.jsx component has mounted, which means it was successfully lazy loaded. */
export const appMounted = (state) => state[CONFIG_SLICE_NAME].appMounted;

/**  The successful completion of the config fetch call
 * @returns {boolean} True when the config fetch completes successfully */
export const success = (state) => state[CONFIG_SLICE_NAME].success;

/** Error messages related to the client config fetch call.
 * Non-null values indicate the client config fetch failed.
 * @returns {string|null} Error message when present, or null if no error is present */
export const error = (state) => state[CONFIG_SLICE_NAME].error;

/** An object of all configurable values. This is considered the large "bucket"
 * of unorganized configurable values. Should be used sparingly, prefer more
 * granular selectors when possible.
 * @returns {Object} configSlice.values */
export const values = (state) => state[CONFIG_SLICE_NAME].values;

/** @returns {number} The default search radius for the embold client */
export const defaultSearchRadius = (state) => state[CONFIG_SLICE_NAME].values.DEFAULT_SEARCH_RADIUS;

/** @returns {number} The corresponding sliderValue for the default radius
 * RADIUS_TO_SLIDER_VALUE maps our radius options to the Mui slider's scale  */
export const defaultRadiusSliderValue = createSelector(
  [defaultSearchRadius],
  (radiusValue) => RADIUS_TO_SLIDER_VALUE[radiusValue]
);

/** @returns {string} The name of the client config JSON file to fetch */
export const name = (state) => state[CONFIG_SLICE_NAME].values.CONFIG_NAME;

const termsLastAccepted = (state) => state[CONFIG_SLICE_NAME].termsLastAccepted;

export const isTermsAccepted = createSelector([termsLastAccepted], (termsLastAccepted) => {
  if (!termsLastAccepted) return false;
  let dateLastAccepted;
  try {
    dateLastAccepted = new Date(termsLastAccepted);
  } catch {
    return false;
  }

  if (dateLastAccepted < dateTermsLastUpdated) {
    // terms have been updated since the user last accepted them
    return false;
  }

  const now = new Date();
  const diffInMs = now - dateLastAccepted;
  const daysOld = diffInMs / (1000 * 60 * 60 * 24);

  return daysOld < MAX_DAYS_BETWEEN_TERMS_ACCEPTANCE;
});

/**
 * Closed access clients require an integration token to be created and stored
 * on the member session table. Without an integration token that exists in the
 * table, API calls will return a 401 with a redirect URL.
 *
 * This is set on the backend client config
 * @returns {boolean} True for clients that require authentication */
export const isClosedAccess = (state) => state[CONFIG_SLICE_NAME].values.CLOSED_ACCESS;
export const apiUrl = (state) => state[CONFIG_SLICE_NAME].values.API_URL;

/**  @returns {string} The base url for map links */
export const mapsUrl = (state) => state[CONFIG_SLICE_NAME].values.MAPS_URL;

/**  @returns {string} The URL used to authenticate a user */
export const redirectUrl = (state) => state[CONFIG_SLICE_NAME].values.REDIRECT_URL;

/**  @returns {string} local client */
export const localClientToUse = (state) => {
  if (state[CONFIG_SLICE_NAME].localClientToUse) {
    return state[CONFIG_SLICE_NAME].localClientToUse;
  }

  return 'walmart';
};

/**
 * Used to display auth-protected content (eg. "support" link) on public routes
 */
export const isLoggedIn = createSelector(
  [
    isTermsAccepted,
    currentSlug,
    isClosedAccess,
    selectSession.hasAccessToken,
    requireReferringClinic,
    referringClinic,
  ],
  (
    isTermsAccepted,
    networkSlug,
    isClosedAccess,
    hasAccessToken,
    referringClinicIsRequired,
    currentReferringClinic
  ) => {
    if (!isTermsAccepted || !networkSlug) return false;
    if (isClosedAccess && !hasAccessToken) return false;
    if (referringClinicIsRequired) {
      return Boolean(currentReferringClinic);
    }
    return true;
  }
);
